import { useEffect, useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import NavigationArrow from './NavigationArrow';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

type Props<T> = {
  settings: Settings & {
    cardSpacing?: string;
    prevBtnStyle?: string;
    nextBtnStyle?: string;
    prevArrowStyle?: string;
    nextArrowStyle?: string;
    slideOnKeydown?: boolean;
  };
  renderItem: (item: T, prefetchImages: boolean, index: number) => JSX.Element;
  items: Array<T & { id?: number | string }>;
};

const Carousel = <T,>({ settings, renderItem, items }: Props<T>) => {
  const ref = useRef<Slider>(null);
  const [prefetchImages, setPrefetchImages] = useState<boolean>(false);

  const {
    prevBtnStyle,
    nextBtnStyle,
    prevArrowStyle,
    nextArrowStyle,
    cardSpacing,
    slideOnKeydown,
    ...restSettings
  } = settings;

  const carouselSettings = {
    ...restSettings,
    prevArrow: (
      <NavigationArrow
        arrowType="prev"
        prevBtnStyle={prevBtnStyle}
        prevArrowStyle={prevArrowStyle}
      />
    ),
    nextArrow: (
      <NavigationArrow
        arrowType="next"
        nextBtnStyle={nextBtnStyle}
        nextArrowStyle={nextArrowStyle}
      />
    ),
  };

  useEffect(() => {
    const keydownEvents = (e: KeyboardEvent) => {
      if (!slideOnKeydown) return;

      if (e.key === 'ArrowRight') {
        ref.current?.slickNext();
      }
      if (e.key === 'ArrowLeft') {
        ref.current?.slickPrev();
      }
    };
    window.addEventListener('keydown', keydownEvents);
    return () => window.removeEventListener('keydown', keydownEvents);
  }, [slideOnKeydown]);

  return (
    <div
      // trigger setPrefetchImages when user interacts with the carousel
      onMouseOver={() => {
        setPrefetchImages(true);
      }}
      onFocus={() => {
        setPrefetchImages(true);
      }}
      onTouchStart={() => {
        setPrefetchImages(true);
      }} // For detecting user touch on mobile and tablet.
    >
      {/* because this is a wrapper around react-slick */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Slider {...carouselSettings} ref={ref}>
        {items.map((item, index) => (
          <div key={item.id} className={cardSpacing}>
            {renderItem(item, prefetchImages, index)}
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default Carousel;
